import React from 'react';
import { X } from 'lucide-react';
import type { MarketReviewFilter } from '../../types/market-review';

interface MarketReviewFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: MarketReviewFilter;
  onFilterChange: (filters: Partial<MarketReviewFilter>) => void;
  reviewTypes: string[];
  lots: string[];
  reviewTypeMap?: Record<string, string>;
}

export default function MarketReviewFilterPanel({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  reviewTypes,
  lots,
  reviewTypeMap = {}
}: MarketReviewFilterPanelProps) {
  if (!isOpen) return null;

  return (
    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Filters</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lot
            </label>
            <select
              value={filters.lot || ''}
              onChange={(e) => onFilterChange({ lot: e.target.value || null })}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">All Lots</option>
              {lots.map((lot) => (
                <option key={lot} value={lot}>
                  {lot}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Type
            </label>
            <select
              value={filters.reviewType || ''}
              onChange={(e) => onFilterChange({ reviewType: e.target.value || null })}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">All Types</option>
              {reviewTypes.map((type) => (
                <option key={type} value={type}>
                  {reviewTypeMap[type] || type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Date After
            </label>
            <input
              type="date"
              value={filters.reviewDateAfter || ''}
              onChange={(e) => onFilterChange({ reviewDateAfter: e.target.value || null })}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Review Date Before
            </label>
            <input
              type="date"
              value={filters.reviewDateBefore || ''}
              onChange={(e) => onFilterChange({ reviewDateBefore: e.target.value || null })}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Next Review Before
            </label>
            <input
              type="date"
              value={filters.nextReviewBefore || ''}
              onChange={(e) => onFilterChange({ nextReviewBefore: e.target.value || null })}
              className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="pt-4 border-t">
            <button
              onClick={() => onFilterChange({
                lot: null,
                reviewType: null,
                reviewDateAfter: null,
                reviewDateBefore: null,
                nextReviewBefore: null
              })}
              className="w-full px-4 py-2 text-sm text-primary hover:bg-primary/5 rounded-md"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
